import { useState, useRef, useEffect } from "react";
import { motion } from "framer-motion";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useAuth } from "../context/AuthContext";
import api from "../services/api";
import { toast } from "react-toastify";
import Input from "../components/ui/Input";
import Button from "../components/ui/Button";
import Avatar from "../components/ui/Avatar";
import {
  AccountCircle,
  Email,
  Phone,
  Lock,
  PhotoCamera,
  Business,
} from "@mui/icons-material";

// Dynamic validation schema based on whether user has Outlook account
const getValidationSchema = (hasOutlookAccount) => {
  return Yup.object({
    name: Yup.string()
      .min(2, "Name must be at least 2 characters")
      .required("Name is required"),
    email: Yup.string()
      .email("Invalid email address")
      .required("Email is required"),
    phone: Yup.string().matches(/^[0-9]{10}$/, "Phone number must be 10 digits"),
    currentPassword: hasOutlookAccount
      ? Yup.string() // Optional for Outlook users
      : Yup.string().min(
          6,
          "Password must be at least 6 characters"
        ),
    newPassword: Yup.string()
      .min(6, "Password must be at least 6 characters")
      .when("currentPassword", {
        is: (val) => val && val.length > 0,
        then: (schema) => schema.required("New password is required"),
      })
      .when([], {
        is: () => hasOutlookAccount,
        then: (schema) => schema.when("newPassword", {
          is: (val) => val && val.length > 0,
          then: (schema) => schema.required("New password is required"),
        }),
      }),
    confirmPassword: Yup.string().when("newPassword", {
      is: (val) => val && val.length > 0,
      then: (schema) =>
        schema
          .required("Confirm password is required")
          .oneOf([Yup.ref("newPassword")], "Passwords must match"),
    }),
  });
};

export default function Profile() {
  const { user, updateUser } = useAuth();
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [previewImage, setPreviewImage] = useState(null);
  const fileInputRef = useRef(null);
  const [departments, setDepartments] = useState([]);
  const [useForgotPasswordFlow, setUseForgotPasswordFlow] = useState(false);
  const [generatedOtp, setGeneratedOtp] = useState("");
  const [otpRequestLoading, setOtpRequestLoading] = useState(false);

  // Check if user is employee (email should not be editable)
  const userRole = user?.role?.toUpperCase() || user?.role;
  const isEmployee = userRole === "EMPLOYEE" || user?.role === "employee";
  const isAdmin = userRole === "ADMIN" || userRole === "HR";
  const isManager = userRole === "MANAGER" || user?.role === "manager";
  // All users (admin, manager, employee) can select their department
  const canSelectDepartment = true;
  
  // Check if user logged in via Outlook (they may not know their current password)
  // Use server profile so outlookAccountId is correct even if auth context was loaded from old session
  const [profileUser, setProfileUser] = useState(null);
  const hasOutlookAccount = !!(profileUser?.outlookAccountId ?? user?.outlookAccountId);

  useEffect(() => {
    // Fetch profile so we have outlookAccountId for password section (Outlook users can set password)
    let cancelled = false;
    const fetchProfile = async () => {
      try {
        const res = await api.get("/profile");
        const profile = res.data?.user ?? res.data;
        if (profile && !cancelled) {
          setProfileUser(profile);
          updateUser(profile);
        }
      } catch (err) {
        if (!cancelled) console.error("Profile fetch failed", err);
      }
    };
    if (user?.id || user?._id) fetchProfile();
    return () => { cancelled = true; };
  }, [user?.id]);

  useEffect(() => {
    // Fetch departments for all users
    console.log("Profile: Fetching departments for user...");
    fetchDepartments();
  }, [user]);

  const fetchDepartments = async () => {
    try {
      console.log("Profile: Fetching departments...");
      const response = await api.get("/departments");
      const deptData = Array.isArray(response.data)
        ? response.data
        : Array.isArray(response.data?.data)
        ? response.data.data
        : [];
      console.log("Profile: Fetched departments:", deptData.length);
      setDepartments(deptData);
    } catch (err) {
      console.error("Failed to fetch departments", err);
      toast.error("Failed to load departments");
    }
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith("image/")) {
      toast.error("Please select an image file");
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Image size must be less than 5MB");
      return;
    }

    setUploadingPhoto(true);
    try {
      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreviewImage(reader.result);
      };
      reader.readAsDataURL(file);

      // Upload to server
      const formData = new FormData();
      formData.append("profile_image", file);

      const response = await api.post("/profile/photo", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      updateUser(response.data.user);
      toast.success("Profile photo updated successfully!");
      // Use returned URL immediately so avatar updates even if state propagation lags.
      const uploadedImageUrl = response.data?.user?.profile_image;
      setPreviewImage(uploadedImageUrl || null);
    } catch (err) {
      console.error("Failed to upload photo", err);
      toast.error(err.response?.data?.message || "Failed to upload photo");
      setPreviewImage(null);
    } finally {
      setUploadingPhoto(false);
    }
  };

  const formik = useFormik({
    initialValues: {
      name: user?.name || "",
      email: user?.email || "",
      phone: user?.phone || "",
      department_id: user?.department_id || "",
      currentPassword: "",
      newPassword: "",
      confirmPassword: "",
      otp: "",
    },
    validationSchema: getValidationSchema(hasOutlookAccount),
    enableReinitialize: true,
    onSubmit: async (values, { setSubmitting }) => {
      setError("");
      setSuccess("");

      try {
        const updateData = {
          name: values.name,
          phone: values.phone,
        };

        // Only allow email update for non-employees (admin/manager)
        // Employees cannot change email as it's tied to their login
        if (!isEmployee) {
          updateData.email = values.email;
        }

        // Allow department selection for all users
        if (values.department_id !== undefined) {
          updateData.department_id = values.department_id || null;
        }

        // Handle password change
        // For Outlook users: allow setting password without current password
        // For regular users: require current password or OTP (forgot flow)
        if (values.newPassword) {
          if (hasOutlookAccount) {
            // Outlook users can set password without current password
            updateData.newPassword = values.newPassword;
            updateData.setPasswordForOutlookUser = true;
          } else if (useForgotPasswordFlow && values.otp) {
            if (values.otp.length !== 6 || !/^\d{6}$/.test(values.otp)) {
              setError("Please enter the 6-digit OTP");
              setSubmitting(false);
              return;
            }
            if (values.newPassword !== values.confirmPassword) {
              setError("New password and confirm password must match");
              setSubmitting(false);
              return;
            }
            // Forgot current password: use OTP
            updateData.otp = values.otp;
            updateData.newPassword = values.newPassword;
          } else if (values.currentPassword) {
            // Regular users need current password
            updateData.currentPassword = values.currentPassword;
            updateData.newPassword = values.newPassword;
          }
        }

        await api.put("/profile", updateData);
        const passwordWasSet = !!values.newPassword;

        // Refetch profile to get fresh hasPasswordSet from server (ensures UI shows correct status)
        try {
          const profileRes = await api.get("/profile");
          const profile = profileRes.data?.user ?? profileRes.data;
          if (profile) {
            const userWithPasswordStatus = {
              ...profile,
              hasPasswordSet: profile?.hasPasswordSet ?? passwordWasSet,
            };
            setProfileUser(userWithPasswordStatus);
            updateUser(userWithPasswordStatus);
          }
        } catch (refetchErr) {
          // Fallback: optimistically update if we just set a password
          if (passwordWasSet && profileUser) {
            const withPassword = { ...profileUser, hasPasswordSet: true };
            setProfileUser(withPassword);
            updateUser(withPassword);
          }
        }

        setSuccess("Profile updated successfully!");
        toast.success(
          passwordWasSet
            ? "Password set successfully! You can now log in with email and password."
            : "Profile updated successfully!"
        );

        // Reset password fields and OTP flow
        formik.setFieldValue("currentPassword", "");
        formik.setFieldValue("newPassword", "");
        formik.setFieldValue("confirmPassword", "");
        formik.setFieldValue("otp", "");
        if (useForgotPasswordFlow) {
          setUseForgotPasswordFlow(false);
          setGeneratedOtp("");
        }
      } catch (err) {
        const errorMessage =
          err.response?.data?.message || "Failed to update profile";
        setError(errorMessage);
        toast.error(errorMessage);
      } finally {
        setSubmitting(false);
      }
    },
  });

  return (
    <div className="max-w-4xl mx-auto">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
      >
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-primary mb-2">Edit Profile</h1>
          <p className="text-gray-600">Update your profile information</p>
        </div>

        {/* Profile Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="card mb-6"
        >
          <div className="flex items-center gap-6 p-6">
            <div className="relative">
              <Avatar
                src={previewImage || user?.profile_image}
                name={user?.name}
                size="lg"
              />
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={handlePhotoUpload}
              />
              <motion.button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploadingPhoto}
                className="absolute bottom-0 right-0 w-10 h-10 bg-primary text-white rounded-full flex items-center justify-center shadow-lg hover:bg-primary/90 transition-colors disabled:opacity-50"
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
                title="Change photo"
              >
                {uploadingPhoto ? (
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  <PhotoCamera className="w-5 h-5" />
                )}
              </motion.button>
            </div>
            <div>
              <h2 className="text-2xl font-bold text-primary mb-1">
                {user?.name || "User"}
              </h2>
              <p className="text-sm text-gray-500">
                {user?.role?.toUpperCase() || "EMPLOYEE"}
              </p>
            </div>
          </div>
        </motion.div>

        {/* Error/Success Messages */}
        {error && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-6 p-4 bg-red-50 border border-red-200 rounded-2xl"
          >
            <p className="text-sm text-red-600">{error}</p>
          </motion.div>
        )}
        {success && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-6 p-4 bg-green-50 border border-green-200 rounded-2xl"
          >
            <p className="text-sm text-green-600">{success}</p>
          </motion.div>
        )}

        {/* Form */}
        <form onSubmit={formik.handleSubmit}>
          {/* Personal Information */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="card mb-6"
          >
            <h3 className="text-xl font-bold text-primary mb-6 pb-4 border-b border-gray-200">
              Personal Information
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <AccountCircle className="w-5 h-5 text-gray-400" />
                  <label className="text-sm font-semibold text-gray-700">
                    Full Name
                  </label>
                </div>
                <Input
                  id="name"
                  name="name"
                  type="text"
                  value={formik.values.name}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={
                    formik.touched.name && Boolean(formik.errors.name)
                      ? formik.errors.name
                      : ""
                  }
                  placeholder="Enter your full name"
                />
              </div>

              <div>
                <div className="flex items-center gap-2 mb-2">
                  <Email className="w-5 h-5 text-gray-400" />
                  <label className="text-sm font-semibold text-gray-700">
                    Email Address
                    {isEmployee && (
                      <span className="ml-2 text-xs text-gray-500 font-normal">
                        (Cannot be changed)
                      </span>
                    )}
                  </label>
                </div>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  value={formik.values.email}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  disabled={isEmployee}
                  error={
                    formik.touched.email && Boolean(formik.errors.email)
                      ? formik.errors.email
                      : ""
                  }
                  placeholder="Enter your email"
                  className={isEmployee ? "bg-gray-100 cursor-not-allowed" : ""}
                />
                {isEmployee && (
                  <p className="mt-1 text-xs text-gray-500">
                    Email address cannot be changed as it's linked to your login
                    credentials. Only @scalecapacity.com emails are allowed.
                  </p>
                )}
              </div>

              <div>
                <div className="flex items-center gap-2 mb-2">
                  <Phone className="w-5 h-5 text-gray-400" />
                  <label className="text-sm font-semibold text-gray-700">
                    Phone Number
                  </label>
                </div>
                <Input
                  id="phone"
                  name="phone"
                  type="tel"
                  value={formik.values.phone}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={
                    formik.touched.phone && Boolean(formik.errors.phone)
                      ? formik.errors.phone
                      : ""
                  }
                  placeholder="Enter your phone number"
                />
              </div>

              <div>
                <div className="flex items-center gap-2 mb-2">
                  <Business className="w-5 h-5 text-gray-400" />
                  <label className="text-sm font-semibold text-gray-700">
                    Department <span className="text-gray-500 font-normal">(Optional)</span>
                  </label>
                </div>
                <select
                  id="department_id"
                  name="department_id"
                  value={formik.values.department_id || ""}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  className="input-field w-full"
                >
                  <option value="">Select Department (Optional)</option>
                  {departments.length === 0 ? (
                    <option value="" disabled>No departments available</option>
                  ) : (
                    departments.map((dept) => (
                      <option
                        key={dept.id || dept._id}
                        value={dept.id || dept._id}
                      >
                        {dept.name}
                      </option>
                    ))
                  )}
                </select>
                {departments.length === 0 && (
                  <p className="mt-1 text-xs text-yellow-600">
                    ⚠️ No departments found. Please contact admin to create departments.
                  </p>
                )}
                {departments.length > 0 && (
                  <p className="mt-1 text-xs text-gray-500">
                    {isAdmin || isManager 
                      ? "Select a department to filter employees by department in the Employees page"
                      : "Select your department"}
                  </p>
                )}
              </div>
            </div>
          </motion.div>

          <div className="hidden">
          {/* Change Password */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="card mb-6"
          >
            <h3 className="text-xl font-bold text-primary mb-6 pb-4 border-b border-gray-200">
              {hasOutlookAccount ? "Set Password" : "Change Password"} (Optional)
            </h3>
            {/* Password status - user can see if they have email/password login (actual password cannot be displayed - stored hashed) */}
            <div className="mb-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
              <div className="flex items-center gap-2 mb-1">
                <Lock className="w-5 h-5 text-gray-500" />
                <span className="text-sm font-semibold text-gray-700">
                  Current password status
                </span>
              </div>
              <p className="text-sm text-gray-600">
                {profileUser?.hasPasswordSet ? (
                  <>
                    <span className="font-mono text-gray-800">••••••••</span>{" "}
                    Password is set. You can log in with email and password.
                  </>
                ) : hasOutlookAccount ? (
                  "No password set. You log in with Outlook. Set a password below to also enable email/password login."
                ) : (
                  "No password set. Set a password below to enable email/password login."
                )}
              </p>
            </div>
            {hasOutlookAccount && (
              <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="text-sm text-blue-700">
                  💡 You logged in with Outlook. You can set a password here to enable email/password login. 
                  Current password is not required.
                </p>
              </div>
            )}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {!hasOutlookAccount && !useForgotPasswordFlow && (
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <Lock className="w-5 h-5 text-gray-400" />
                    <label className="text-sm font-semibold text-gray-700">
                      Current Password
                    </label>
                    <button
                      type="button"
                      onClick={() => setUseForgotPasswordFlow(true)}
                      className="text-sm text-primary hover:underline ml-auto"
                    >
                      Forgot current password?
                    </button>
                  </div>
                  <Input
                    id="currentPassword"
                    name="currentPassword"
                    type="password"
                    value={formik.values.currentPassword}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={
                      formik.touched.currentPassword &&
                      Boolean(formik.errors.currentPassword)
                        ? formik.errors.currentPassword
                        : ""
                    }
                    placeholder="Enter current password"
                  />
                </div>
              )}

              {!hasOutlookAccount && useForgotPasswordFlow && (
                <div className="md:col-span-2 space-y-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
                  <p className="text-sm text-gray-700">
                    Generate a one-time code to change your password. The code will be shown here (email not configured yet).
                  </p>
                  <div className="flex flex-wrap items-center gap-2">
                    <Button
                      type="button"
                      variant="primary"
                      disabled={otpRequestLoading}
                      onClick={async () => {
                        setOtpRequestLoading(true);
                        try {
                          const res = await api.post("/profile/request-password-change-otp");
                          const otp = res.data?.otp ?? res.data?.data?.otp;
                          if (otp) {
                            setGeneratedOtp(otp);
                            toast.success("OTP generated");
                          } else {
                            toast.error("Failed to get OTP");
                          }
                        } catch (err) {
                          toast.error(err.response?.data?.message || "Failed to generate OTP");
                        } finally {
                          setOtpRequestLoading(false);
                        }
                      }}
                    >
                      {otpRequestLoading ? "Generating..." : "Generate OTP"}
                    </Button>
                    <button
                      type="button"
                      onClick={() => {
                        setUseForgotPasswordFlow(false);
                        setGeneratedOtp("");
                        formik.setFieldValue("otp", "");
                      }}
                      className="text-sm text-gray-600 hover:underline"
                    >
                      I know my password
                    </button>
                  </div>
                  {generatedOtp && (
                    <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                      <p className="text-sm text-amber-800 font-medium">Your OTP: <span className="text-lg font-mono">{generatedOtp}</span></p>
                      <p className="text-xs text-amber-700 mt-1">Enter this OTP below and set your new password. OTP is valid for 10 minutes.</p>
                    </div>
                  )}
                  {generatedOtp && (
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-1">Enter OTP</label>
                      <Input
                        id="otp"
                        name="otp"
                        type="text"
                        inputMode="numeric"
                        maxLength={6}
                        value={formik.values.otp}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        error={formik.touched.otp && formik.errors.otp ? formik.errors.otp : ""}
                        placeholder="6-digit code"
                      />
                    </div>
                  )}
                </div>
              )}

              <div>
                <div className="flex items-center gap-2 mb-2">
                  <Lock className="w-5 h-5 text-gray-400" />
                  <label className="text-sm font-semibold text-gray-700">
                    New Password
                  </label>
                </div>
                <Input
                  id="newPassword"
                  name="newPassword"
                  type="password"
                  value={formik.values.newPassword}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={
                    formik.touched.newPassword &&
                    Boolean(formik.errors.newPassword)
                      ? formik.errors.newPassword
                      : ""
                  }
                  placeholder="Enter new password"
                />
              </div>

              <div>
                <div className="flex items-center gap-2 mb-2">
                  <Lock className="w-5 h-5 text-gray-400" />
                  <label className="text-sm font-semibold text-gray-700">
                    Confirm New Password
                  </label>
                </div>
                <Input
                  id="confirmPassword"
                  name="confirmPassword"
                  type="password"
                  value={formik.values.confirmPassword}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={
                    formik.touched.confirmPassword &&
                    Boolean(formik.errors.confirmPassword)
                      ? formik.errors.confirmPassword
                      : ""
                  }
                  placeholder="Confirm new password"
                />
              </div>
            </div>
          </motion.div>
          </div>

          {/* Submit Button */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="flex justify-end gap-4"
          >
            <Button
              type="submit"
              variant="primary"
              disabled={formik.isSubmitting}
              className="min-w-[200px]"
            >
              {formik.isSubmitting ? "Updating..." : "Update Profile"}
            </Button>
          </motion.div>
        </form>
      </motion.div>
    </div>
  );
}
